import type { Community } from '@communities/community/domain';
import { UserRoles } from '@core/domain';
import { Icon, IconSize, Menu, MenuItem, MoreActions } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './NavigationSlideout.css';

interface CommunityListProps {
  communities: Community[];
  hasRoleAccess: (roleType: UserRoles, communityGroupId?: string, communityId?: string) => boolean;
  onCommunityClick: (communityId: string) => void;
  onMenuItemClick: (path: string) => void;
}

/**
 * Community list component
 * Displays the list of communities with action menus
 */
export function CommunityList({ communities, hasRoleAccess, onCommunityClick, onMenuItemClick }: CommunityListProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleMenuToggle = (communityId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(prev => (prev === communityId ? null : communityId));
  };

  if (!communities || communities.length === 0) {
    return <div className={styles.emptyState}>No communities found</div>;
  }

  return (
    <>
      {communities.map(community => (
        <div key={community.id} className={styles.communityItemWrapper}>
          <button
            className={styles.communityItem}
            onClick={() => onCommunityClick(community.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCommunityClick(community.id);
              }
            }}
          >
            <div className={styles.communityImageContainer}>
              {community.logoUrl ? (
                <img alt={`${community.name} logo`} className={styles.communityImage} src={community.logoUrl} />
              ) : (
                <div className={styles.communityImagePlaceholder}>{community.name.charAt(0)}</div>
              )}
            </div>
            <div className={styles.communityContent}>
              <div className={styles.communityName}>{community.name}</div>
            </div>
          </button>

          {/* More actions button */}
          <button
            aria-expanded={openMenuId === community.id}
            aria-label={`More actions for ${community.name}`}
            className={styles.moreActionsButton}
            id={`more-actions-${community.id}`}
            onClick={e => handleMenuToggle(community.id, e)}
          >
            <Icon icon={MoreActions} iconSize={IconSize.Small} />
          </button>

          {/* Dropdown menu */}
          <Menu
            isOpen={openMenuId === community.id}
            placement="bottom end"
            showCloseButton={false}
            triggerId={`more-actions-${community.id}`}
            onOpenChange={(isOpen: boolean) => {
              if (!isOpen) {
                setOpenMenuId(null);
              }
            }}
          >
            <MenuItem label="View Details" onClick={() => onMenuItemClick(`/communities/${community.id}`)} />
            <MenuItem label="Test Classification" onClick={() => onMenuItemClick(`/communities/${community.id}/classification-testing`)} />
            {hasRoleAccess(UserRoles.QA, community.communityGroupId, community.id) && (
              <MenuItem label="View Audits" onClick={() => onMenuItemClick(`/communities/${community.id}/audits`)} />
            )}
            <MenuItem label="Search Content" onClick={() => onMenuItemClick(`/communities/${community.id}/content-search`)} />
          </Menu>
        </div>
      ))}
    </>
  );
}
